import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Vote, CheckCircle2, Upload, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", address: "", child_class: "", consent_rgpd: false, consent_image: false };

export default function ListeCetf() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [media, setMedia] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/liste-cetf/public-info").then((r) => setInfo(r.data)).catch(() => setInfo({ candidates_count: 0, interim_referent: null, candidacy_open: true, next_school_year: "", list_title: "Liste CET", list_subtitle: "", academy_label: "Académie d'Orléans-Tours", title_color: "#065F46", title_font: "Plus Jakarta Sans", closed_title: "Dépôt de candidature clôturé", closed_message: "" }));
    api.get("/cetf-media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);

  const candidacyClosed = info !== null && info.candidacy_open === false;

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Veuillez joindre votre pièce d'identité.");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("file", file);
      await api.post("/liste-cetf/candidacy", data);
      setDone(true);
      toast.success("Candidature envoyée ! Le bureau l'étudiera prochainement.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-emerald-50/80 to-white" data-testid="liste-cetf-page">
      <div className="liste-watermark" style={{ backgroundImage: "url(/assets/logo-tree.png)" }} aria-hidden="true" />
      <div className="relative z-[1] max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        <div className="relative text-center space-y-4">
          <div className="marianne-slide flex items-center justify-center sm:justify-end gap-3 pb-2 sm:pb-0 sm:absolute sm:right-0 sm:-top-3" data-testid="liste-cet-academy">
            <img src="/assets/marianne.png" alt="Marianne — République française" className="h-12 w-auto object-contain" />
            <span className="text-left leading-tight">
              <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500">République française</span>
              <span className="block text-xs font-semibold text-slate-700" data-testid="liste-cet-academy-label">{info?.academy_label || "Académie d'Orléans-Tours"}</span>
            </span>
          </div>
          <div className="h-14 w-14 rounded-2xl bg-emerald-600 flex items-center justify-center mx-auto shadow-lg">
            <Vote className="h-7 w-7 text-white" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Élections des représentants de parents d'élèves</p>
          <h1
            className="liste-title-slide text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ color: info?.title_color || "#065F46", fontFamily: `'${info?.title_font || "Plus Jakarta Sans"}', sans-serif` }}
            data-testid="liste-cetf-title"
          >{info?.list_title || "Liste CET"}</h1>
          <p className="text-sm font-semibold text-emerald-700" data-testid="liste-cetf-subtitle">
            {info?.list_subtitle || "Liste de parents d'élèves — soutenue par Cœur d'École Toury Famille association"}
          </p>
          <p className="text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Aux élections des représentants de parents d'élèves, ce sont <strong>les parents</strong> qui constituent leur
            propre liste — avec son nom et son logo. Deux parents ou plus peuvent se regrouper en <strong>association de
            fait</strong> (association non déclarée, loi de 1901) entre parents d'élèves de l'école élémentaire Julien
            Mercier de Toury pour présenter cette liste. L'association CETF <strong>soutient</strong> la liste et
            l'accompagne, sans se substituer à elle : la liste appartient aux parents.
          </p>
          <p className="text-xs text-slate-400 leading-relaxed max-w-2xl mx-auto" data-testid="liste-cetf-sources">
            Sources : règles des élections de représentants de parents d'élèves —{" "}
            <a href="https://www.service-public.gouv.fr/particuliers/vosdroits/F1389" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-700">service-public.fr</a>
            {" "}·{" "}
            <a href="https://eduscol.education.gouv.fr/5109/la-representation-des-parents-d-eleves" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-700">Éduscol — La représentation des parents d'élèves</a>.
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-sm">
            <span className="bg-white border border-emerald-200 rounded-full px-4 py-1.5 text-emerald-800 font-medium" data-testid="liste-cetf-count">
              {info ? `${info.candidates_count} candidat(s) validé(s)` : "…"}
            </span>
            {info?.interim_referent && (
              <span className="bg-white border border-emerald-200 rounded-full px-4 py-1.5 text-emerald-800 font-medium" data-testid="liste-cetf-referent">
                Référente de liste par intérim : {info.interim_referent}
              </span>
            )}
          </div>
          {user && (
            <Button variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" asChild data-testid="liste-cetf-space-link">
              <Link to="/liste-cetf/espace">Accéder à l'espace {info?.list_title || "Liste CET"} <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
          )}
        </div>

        <Card className="border-emerald-200 shadow-lg" data-testid="candidacy-card">
          <CardContent className="p-6 sm:p-8">
            {done ? (
              <div className="text-center space-y-3 py-6" data-testid="candidacy-success">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
                <h2 className="text-xl font-bold text-slate-900">Candidature bien reçue !</h2>
                <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                  Un email de confirmation vient de vous être envoyé : votre candidature est en attente de validation
                  par le bureau, qui pourra vous proposer un entretien. Une fois validée, vous pourrez accéder à
                  l'espace « {info?.list_title || "Liste CET"} » avec votre compte (celui associé à l'email indiqué).
                </p>
              </div>
            ) : candidacyClosed ? (
              <div className="text-center space-y-4 py-6" data-testid="candidacy-closed">
                <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                  <Vote className="h-6 w-6 text-slate-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900" data-testid="candidacy-closed-title">{info.closed_title || "Dépôt de candidature clôturé"}</h2>
                <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed" data-testid="candidacy-closed-message">
                  {info.closed_message || "Le dépôt de candidature aux élections des représentants de parents d'élèves est clôturé pour l'année scolaire en cours."}
                </p>
                <p className="text-base font-semibold text-emerald-700" data-testid="candidacy-closed-next-year">
                  Rendez-vous pour l'année scolaire {info.next_school_year}.
                </p>
                <p className="text-xs text-slate-400">Association de parents d'élèves — Liste CET</p>
                <p className="text-xs text-slate-400 leading-relaxed max-w-md mx-auto">
                  Le calendrier électoral est fixé chaque année par circulaire du ministère de l'Éducation nationale
                  (publication en juin–juillet) : dépôt des candidatures fin septembre, élections début octobre.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="candidacy-form">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Déposer ma candidature</h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Utilisez de préférence l'email de votre compte adhérent : c'est lui qui ouvrira l'accès à l'espace {info?.list_title || "Liste CET"}.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cand-firstname">Prénom *</Label>
                    <Input id="cand-firstname" required maxLength={80} data-testid="candidacy-firstname-input"
                      value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cand-lastname">Nom *</Label>
                    <Input id="cand-lastname" required maxLength={80} data-testid="candidacy-lastname-input"
                      value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cand-email">Email *</Label>
                    <Input id="cand-email" type="email" required data-testid="candidacy-email-input"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cand-phone">Téléphone *</Label>
                    <Input id="cand-phone" type="tel" required maxLength={30} data-testid="candidacy-phone-input"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-class">Classe de l'enfant *</Label>
                  <Input id="cand-class" required maxLength={80} placeholder="Ex. : CE2 — Mme Dupont" data-testid="candidacy-class-input"
                    value={form.child_class} onChange={(e) => setForm({ ...form, child_class: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-address">Adresse postale *</Label>
                  <Input id="cand-address" required maxLength={300} placeholder="N°, rue, code postal, commune" data-testid="candidacy-address-input"
                    value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cand-file">Pièce d'identité * <span className="text-slate-400 font-normal">(image ou PDF, 10 Mo max)</span></Label>
                  <label htmlFor="cand-file" className="flex items-center gap-3 border-2 border-dashed border-emerald-300 rounded-xl p-4 cursor-pointer hover:bg-emerald-50/50 transition-colors" data-testid="candidacy-file-drop">
                    <Upload className="h-5 w-5 text-emerald-600 shrink-0" />
                    <span className="text-sm text-slate-600">{file ? file.name : "Cliquez pour joindre votre pièce d'identité"}</span>
                  </label>
                  <input id="cand-file" type="file" accept="image/*,.pdf" className="hidden" data-testid="candidacy-file-input"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  <p className="text-xs text-slate-400">Elle permet au bureau de vérifier votre identité. Elle n'est visible que par le bureau.</p>
                </div>
                <div className="space-y-3 bg-slate-50 rounded-xl p-4">
                  <div className="flex items-start gap-2.5">
                    <Checkbox id="cand-rgpd" className="mt-0.5" data-testid="candidacy-rgpd-checkbox"
                      checked={form.consent_rgpd} onCheckedChange={(v) => setForm({ ...form, consent_rgpd: v === true })} />
                    <Label htmlFor="cand-rgpd" className="text-sm font-normal leading-snug text-slate-600 cursor-pointer">
                      J'accepte que mes informations soient utilisées pour la gestion de ma candidature (consentement RGPD). *
                    </Label>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <Checkbox id="cand-image" className="mt-0.5" data-testid="candidacy-image-checkbox"
                      checked={form.consent_image} onCheckedChange={(v) => setForm({ ...form, consent_image: v === true })} />
                    <Label htmlFor="cand-image" className="text-sm font-normal leading-snug text-slate-600 cursor-pointer">
                      J'autorise la diffusion de mon image (photos) sur les supports de communication de la liste et de l'association.
                    </Label>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={sending || !form.consent_rgpd} data-testid="candidacy-submit-btn">
                  {sending ? "Envoi en cours…" : "Envoyer ma candidature"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {media.length > 0 && (
        <section className="mt-8 space-y-4" data-testid="cetf-media-section">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Upload className="h-5 w-5 text-emerald-600" /> Médiathèque de la liste
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {media.map((m) => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/cetf-media-files/${m.storage_path}`;
              if (m.media_type === "image") {
                return (
                  <a key={m.id} href={url} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`cetf-media-${m.id}`}>
                    <img src={url} alt={m.title} className="w-full h-40 object-cover" loading="lazy" />
                    {m.title && <p className="text-xs font-medium text-slate-700 px-3 py-2 truncate">{m.title}</p>}
                  </a>
                );
              }
              if (m.media_type === "video") {
                return (
                  <div key={m.id} className="rounded-2xl overflow-hidden border border-slate-200 bg-black shadow-sm" data-testid={`cetf-media-${m.id}`}>
                    <video src={url} controls className="w-full h-40 object-contain" preload="metadata" />
                    {m.title && <p className="text-xs font-medium text-slate-700 bg-white px-3 py-2 truncate">{m.title}</p>}
                  </div>
                );
              }
              return (
                <a key={m.id} href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-center" data-testid={`cetf-media-${m.id}`}>
                  <span className="text-[10px] font-bold uppercase tracking-widest bg-red-100 text-red-700 rounded-full px-2.5 py-1">PDF</span>
                  <span className="text-xs font-medium text-slate-700 line-clamp-2">{m.title || "Document"}</span>
                </a>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
